import { useMemo } from 'react';
import { useCustomer } from '../contexts/CustomerContext';
import { logger } from '../services/logger';

export interface OperationalCustomer {
  id: string;
  name: string;
  lastAccess: string;
  type: string;
  condoType?: 'apartamento' | 'casas';
  photo_url?: string;
  internal_code?: string;
  portaria_type?: string;
  phase?: string;
}

interface UseOperationalCustomersResult {
  customers: OperationalCustomer[];
  isLoading: boolean;
  error: Error | null;
  refresh: () => Promise<void>;
}

/**
 * Hook to get customers for operational view
 * Uses availableCustomers from CustomerContext which is already filtered by user permissions
 */
export const useOperationalCustomers = (): UseOperationalCustomersResult => {
  const { availableCustomers, isLoading } = useCustomer();

  // Transform available customers to OperationalCustomer format
  const customers = useMemo<OperationalCustomer[]>(() => {
    const transformed = availableCustomers.map((customer) => ({
      id: customer.id,
      name: customer.name,
      lastAccess: '—', // API doesn't provide last access time directly
      type: customer.type || 'condominio',
      condoType: (customer as any).condoType as 'apartamento' | 'casas' | undefined,
      photo_url: customer.photo_url,
      internal_code: customer.internal_code,
      portaria_type: (customer as any).portaria_type,
      phase: (customer as any).phase,
    }));

    logger.debug('useOperationalCustomers', 'Transformed customers from context', { count: transformed.length });
    return transformed;
  }, [availableCustomers]);

  // Refresh is a no-op since CustomerContext manages the data
  const refresh = async () => {
    logger.debug('useOperationalCustomers', 'Refresh called - data managed by CustomerContext');
  };

  return {
    customers,
    isLoading,
    error: null,
    refresh,
  };
};
